import { Injectable, inject } from '@angular/core';
import {NetworkService} from '../../../../../../projects/common/src/lib/services/network.service';
import {BaseApiService} from '../base-api.service';
import {FirebaseApi} from '../../firebase-api.service';
import {Logger} from '../../../utils/logger';
import {errorCodeOf} from '../../../utils/error-code';
import {EmbedAccess, embedDeveloperOf} from '../../../utils/embed-unavailable';
import {ProjectData} from '../../../model/project-data';
import {WebhookResponse} from '../../../model/webhook-data';
import {ApiRestrictedWebsite} from '../../../model/api-restricted-website';

/**
 * What an API key may do. Mirrors ApiScope on the server.
 *
 * Every key used to carry the whole surface, so a key pasted into a public page
 * to render an embed could also delete every project and user on the account.
 */
export enum ApiScope {
  AUTH = 'auth',
  PROJECTS_READ = 'projects:read',
  PROJECTS_WRITE = 'projects:write',
  USERS_READ = 'users:read',
  USERS_WRITE = 'users:write',
  PRODUCTS_READ = 'products:read',
  PRODUCTS_WRITE = 'products:write',
  ACCOUNT_READ = 'account:read',
  GUESTS_READ = 'guests:read',
}

export const ALL_API_SCOPES: ApiScope[] = [
  ApiScope.AUTH,
  ApiScope.PROJECTS_READ,
  ApiScope.PROJECTS_WRITE,
  ApiScope.USERS_READ,
  ApiScope.USERS_WRITE,
  ApiScope.PRODUCTS_READ,
  ApiScope.PRODUCTS_WRITE,
  ApiScope.ACCOUNT_READ,
  ApiScope.GUESTS_READ,
];

/**
 * Scopes that must be ticked to be granted.
 *
 * A key with nothing recorded is treated as having every other scope, so
 * that keys predating scopes keep working. These were added afterwards, so
 * nothing can be relying on them — and the catalogue is what the contractor
 * charges, which is not something an embed key should be able to rewrite.
 * The same goes for the account's spend and the guests' contact details.
 *
 * Mirrors EXPLICIT_ONLY_SCOPES on the server, which is where it is enforced.
 */
export const EXPLICIT_ONLY_SCOPES: ApiScope[] = [
  ApiScope.PRODUCTS_READ,
  ApiScope.PRODUCTS_WRITE,
  ApiScope.ACCOUNT_READ,
  ApiScope.GUESTS_READ,
];

/**
 * Scopes only an Enterprise plan can use: the users endpoints manage an
 * organization's seats, and Open Guest has none. Mirrors the server, which
 * cuts keys to the plan and refuses these on Open Guest.
 */
export const ENTERPRISE_ONLY_SCOPES: ApiScope[] = [
  ApiScope.USERS_READ,
  ApiScope.USERS_WRITE,
];

/**
 * Scopes only an Open Guest plan can use: guests exist only there. Mirrors
 * OPEN_GUEST_ONLY_SCOPES on the server.
 */
export const OPEN_GUEST_ONLY_SCOPES: ApiScope[] = [
  ApiScope.GUESTS_READ,
];

/** Every scope a key on this plan can hold. */
export function scopesForPlan(isEnterprise: boolean): ApiScope[] {
  return isEnterprise
    ? ALL_API_SCOPES.filter(s => OPEN_GUEST_ONLY_SCOPES.indexOf(s) === -1)
    : ALL_API_SCOPES.filter(s => ENTERPRISE_ONLY_SCOPES.indexOf(s) === -1);
}

/** i18n key for each scope's label and one-line explanation. */
export const API_SCOPE_LABELS: {[k in ApiScope]: {label: string, desc: string}} = {
  [ApiScope.AUTH]: {label: 'scope_auth', desc: 'scope_auth_desc'},
  [ApiScope.PROJECTS_READ]: {label: 'scope_projects_read', desc: 'scope_projects_read_desc'},
  [ApiScope.PROJECTS_WRITE]: {label: 'scope_projects_write', desc: 'scope_projects_write_desc'},
  [ApiScope.USERS_READ]: {label: 'scope_users_read', desc: 'scope_users_read_desc'},
  [ApiScope.USERS_WRITE]: {label: 'scope_users_write', desc: 'scope_users_write_desc'},
  [ApiScope.PRODUCTS_READ]: {label: 'scope_products_read', desc: 'scope_products_read_desc'},
  [ApiScope.PRODUCTS_WRITE]: {label: 'scope_products_write', desc: 'scope_products_write_desc'},
  [ApiScope.ACCOUNT_READ]: {label: 'scope_account_read', desc: 'scope_account_read_desc'},
  [ApiScope.GUESTS_READ]: {label: 'scope_guests_read', desc: 'scope_guests_read_desc'},
};

/**
 * Which world a key works in. Mirrors ApiEnvironment on the server.
 *
 * Sandbox is a second set of keys over the same account with its own data:
 * projects made with a sandbox key are visible only to sandbox keys. It is
 * where an integration gets built, so that building it does not leave test
 * projects sitting beside a customer's real ones.
 */
export enum ApiEnvironment {
  LIVE = 'live',
  SANDBOX = 'sandbox',
}

/** One API key as the dashboard's key list sees it. */
export interface DeveloperApiKey {
  key: string;
  name: string;
  createdOn: number;
  lastAccess: number;
  accessCount: number;
  /** When a rotated-out key stops working (ms); 0 means no deadline. */
  expiresAt: number;
  /** Enabled, not expired, and inside any grace deadline. */
  live: boolean;
  restrictions: ApiRestrictedWebsite[];
  /** Reported as the full set for a key that carries none. */
  scopes: ApiScope[];
  /** 'live' unless the key was minted into the sandbox. */
  environment: ApiEnvironment;
  /**
   * Suspended by our staff. While any key of an environment is, no new key
   * can be created there (the server answers api_key_suspended).
   */
  suspended?: boolean;
  /**
   * 'no_scopes': made before keys had access settings, so it can do
   * everything; our staff put it on a deadline (expiresAt), and the owner
   * was told to replace it with a narrower key.
   */
  retireReason?: string;
}

/** What POST /developer/key answers: the new key, and what became of the old ones. */
export interface MintedApiKey {
  key: string;
  name?: string;
  createdOn: number;
  scopes?: ApiScope[];
  environment: ApiEnvironment;
  /** How many working keys in this environment were put on their way out. */
  retiredKeys: number;
  /** When those keys stop being accepted (ms). */
  retiredUntil: number;
}


@Injectable({
  providedIn: 'root',
})
export class DeveloperApiService extends BaseApiService {

  constructor() {

    super();
  }

  /**
   * Mint a new key. The previous ones keep working for `graceHours` so the
   * sites embedding them can be redeployed without an outage; 0 revokes them
   * on the spot, which is what a leaked key needs.
   *
   * Only keys in the SAME environment are retired, so minting a sandbox key
   * to try something cannot take the live key down with it.
   */
  generateApiKey(options: {name?: string, graceHours?: number, scopes?: ApiScope[],
                           environment?: ApiEnvironment} = {}) {
    return this.post<MintedApiKey>('/developer/key', options);
  }

  /** Every key on the account — used to show a rotation still in progress. */
  listKeys() {
    return this.get<{keys: DeveloperApiKey[]}>('/developer/keys');
  }

  /**
   * Replace the websites allowed to use one of this account's keys (empty =
   * any website). The server checks and normalises the hosts, logs the change
   * and emails the owner when websites come off the list.
   */
  setKeyRestrictions(key: string, websites: string[]) {
    return this.put<{restrictions: ApiRestrictedWebsite[]}>(
      `/developer/keys/${encodeURIComponent(key)}/restrictions`, {websites});
  }

  /** Retire one key immediately. Refused if it is the only working one. */
  revokeKey(key: string) {
    return this.delete<{revoked: boolean, key: string}>(
      `/developer/keys/${encodeURIComponent(key)}`, null);
  }

  /**
   * Last 30 days of key usage: per-day counts and the domains calling it.
   * Only live traffic is counted, so a sandbox request answers an empty window.
   */
  getUsage(environment: ApiEnvironment = ApiEnvironment.LIVE) {
    return this.get<{
      windowDays: number,
      total: number,
      byDay: {date: string, count: number}[],
      domains: {host: string, count: number}[],
      key: {createdOn: number, lastAccess: number, accessCount: number, enabled: boolean} | null
    }>('/developer/usage', {}, {params: {environment}});
  }

  // ── Webhooks ─────────────────────────────────────────────────────
  // The signing secret is minted and stored server-side; these calls only ever
  // see a hint of it, except the one response to a rotation.

  getWebhook() {
    return this.get<WebhookResponse>('/developer/webhooks');
  }

  saveWebhook(url: string, events: string[], enabled: boolean) {
    return this.put<WebhookResponse>('/developer/webhooks', {url, events, enabled});
  }

  rotateWebhookSecret() {
    return this.post<WebhookResponse>('/developer/webhooks/secret', null);
  }

  deleteWebhook() {
    return this.delete<{deleted: boolean}>('/developer/webhooks', null);
  }

  testWebhook(event: string) {
    return this.post<WebhookResponse>('/developer/webhooks/test', {event});
  }

  getEnterpriseUserCustomToken(apiKey: string, uid: string) {
    return this.postPublic<any>(`/auth/${uid}/login`, null, {Authorization: apiKey});
  }

  createIFrameCustomToken(apiKey: string) {
    return this.postPublic<any>('/auth/iframe/token', null, {Authorization: apiKey});
  }

  createNewProject(devToken: string, projectInfo, revokeToken: boolean = false) {
    return new Promise<ProjectData>((resolve, reject) => {
      this.post<any>(`/developer/project/new`, {
        token: devToken,
        revokeToken: revokeToken,
        data: projectInfo,
        source: document.referrer
      }).then(project => {
        resolve(project);
      }).catch(reason => {
        Logger.error(reason);
        reject(reason);
      });
    });
  }

  /**
   * May an embed open this project? A refusal keeps the server's reason: it
   * used to come back as a bare `false`, and the visitor got the 403 page
   * whatever the cause (EmbedUnavailablePage now says which).
   */
  checkApiKeyProjectAccess(projectId: string, devToken?: string, devKey?: string, revokeToken: boolean = true) {
    return new Promise<EmbedAccess>((resolve) => {
      this.post<any>(`/developer/project/${projectId}/access`, {
        key: devKey,
        token: devToken,
        revokeToken: revokeToken,
        source: document.referrer
      }).then(result => {
        resolve({
          granted: !!(result && result.granted), errorCode: '',
          authorized: !!(result && result.authorized), pass: !!(result && result.pass),
        });
      }).catch(reason => {
        Logger.error(reason);
        const developer = embedDeveloperOf(reason);
        resolve({granted: false, errorCode: errorCodeOf(reason), ...(developer ? {developer} : {})});
      });
    });
  }
}
